import {
    AnalysisResult,
    AnalysisRequest,
    ProcessingProgress,
    ProcessingStep,
} from '../types';
import {
    createMockAnalysisResult,
    createProcessingSteps,
    createMockProcessingProgress,
    randomizeAnalysisData,
    mockAnalysisData,
} from './mockData';
import { PROCESSING_STEPS } from '../utils/constants';

// Storage key for analysis results
const STORAGE_KEY = 'gis_analysis_results';

// Active analysis tracking
interface ActiveAnalysis {
    analysisId: string;
    fileId: string;
    steps: ProcessingStep[];
    currentStep: number;
    cancelled: boolean;
    callbacks: Set<(progress: ProcessingProgress) => void>;
}

const activeAnalyses = new Map<string, ActiveAnalysis>();

// Generate analysis ID
const generateAnalysisId = (): string => {
    return `analysis-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Analysis Service - handles analysis operations (mock implementation)
 */
export const analysisService = {
    /**
     * Start analysis on an uploaded file
     */
    async startAnalysis(request: AnalysisRequest): Promise<AnalysisResult> {
        const analysisId = generateAnalysisId();
        const steps = createProcessingSteps();

        const activeAnalysis: ActiveAnalysis = {
            analysisId,
            fileId: request.fileId,
            steps,
            currentStep: 0,
            cancelled: false,
            callbacks: new Set(),
        };

        activeAnalyses.set(analysisId, activeAnalysis);

        // Start processing simulation
        this.simulateProcessing(activeAnalysis);

        // Return initial result
        return {
            id: analysisId,
            fileId: request.fileId,
            status: 'processing',
            createdAt: new Date(),
            data: null,
        };
    },

    /**
     * Simulate processing with delays
     */
    async simulateProcessing(analysis: ActiveAnalysis): Promise<void> {
        for (let i = 0; i < PROCESSING_STEPS.length; i++) {
            if (analysis.cancelled) break;

            const stepConfig = PROCESSING_STEPS[i];
            const step = analysis.steps[i];

            // Set step to active
            step.status = 'active';
            step.startedAt = new Date();
            step.progress = 0;
            analysis.currentStep = i + 1;

            // Notify callbacks
            this.notifyProgress(analysis);

            // Simulate step progress
            const progressInterval = setInterval(() => {
                if (analysis.cancelled) {
                    clearInterval(progressInterval);
                    return;
                }
                step.progress = Math.min(step.progress + 10, 100);
                this.notifyProgress(analysis);
            }, stepConfig.duration / 10);

            // Wait for step duration
            await new Promise((resolve) => setTimeout(resolve, stepConfig.duration));
            clearInterval(progressInterval);

            // Mark step complete
            step.status = 'complete';
            step.progress = 100;
            step.completedAt = new Date();
            this.notifyProgress(analysis);
        }

        // Analysis complete - save result
        if (!analysis.cancelled) {
            const result = createMockAnalysisResult(analysis.fileId);
            result.data = randomizeAnalysisData(mockAnalysisData);
            this.saveResult(result);
        }

        // Cleanup
        activeAnalyses.delete(analysis.analysisId);
    },

    /**
     * Notify all callbacks of progress update
     */
    notifyProgress(analysis: ActiveAnalysis): void {
        const progress = createMockProcessingProgress(
            analysis.analysisId,
            analysis.currentStep,
            analysis.steps
        );
        analysis.callbacks.forEach((callback) => callback(progress));
    },

    /**
     * Get analysis progress
     */
    async getProgress(analysisId: string): Promise<ProcessingProgress | null> {
        const analysis = activeAnalyses.get(analysisId);
        if (!analysis) return null;

        return createMockProcessingProgress(
            analysis.analysisId,
            analysis.currentStep,
            analysis.steps
        );
    },

    /**
     * Get analysis result
     */
    async getResult(analysisId: string): Promise<AnalysisResult | null> {
        const results = this.listResultsSync();
        return results.find((r) => r.id === analysisId) || null;
    },

    /**
     * Get latest analysis result
     */
    async getLatestResult(): Promise<AnalysisResult | null> {
        const results = this.listResultsSync();
        if (results.length === 0) return null;
        return results[results.length - 1];
    },

    /**
     * Cancel ongoing analysis
     */
    async cancelAnalysis(analysisId: string): Promise<boolean> {
        const analysis = activeAnalyses.get(analysisId);
        if (!analysis) return false;

        analysis.cancelled = true;
        analysis.callbacks.clear();
        activeAnalyses.delete(analysisId);
        return true;
    },

    /**
     * Subscribe to progress updates
     */
    onProgress(
        analysisId: string,
        callback: (progress: ProcessingProgress) => void
    ): () => void {
        const analysis = activeAnalyses.get(analysisId);
        if (!analysis) return () => { };

        analysis.callbacks.add(callback);

        // Return unsubscribe function
        return () => {
            analysis.callbacks.delete(callback);
        };
    },

    /**
     * Save analysis result to localStorage
     */
    saveResult(result: AnalysisResult): void {
        const results = this.listResultsSync();
        results.push(result);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(results));
    },

    /**
     * List all analysis results (sync)
     */
    listResultsSync(): AnalysisResult[] {
        try {
            const data = localStorage.getItem(STORAGE_KEY);
            if (!data) return [];
            return JSON.parse(data).map((r: AnalysisResult) => ({
                ...r,
                createdAt: new Date(r.createdAt),
                completedAt: r.completedAt ? new Date(r.completedAt) : undefined,
            }));
        } catch {
            return [];
        }
    },

    /**
     * Check if analysis is in progress
     */
    isAnalysisInProgress(analysisId: string): boolean {
        return activeAnalyses.has(analysisId);
    },

    /**
     * Clear all results
     */
    clearAll(): void {
        localStorage.removeItem(STORAGE_KEY);
        activeAnalyses.clear();
    },
};

export default analysisService;
